// Seitenleiste im Eigentümermodus (Handoff Eigentümer §6.6): Name, Einheiten, Rolle (für die
// Abschnitte) und Anzahl je Bereich. Kern wie die Detailseite, sonst nur Zählabfragen (head).

import { useEffect, useState } from 'react';
import { supabase } from '../supabase/client';
import { heuteIso } from '../immobilien/format';
import { portfolioWerte } from './detail';
import { istAktuell } from './einheitEigentum';
import type { RollenWerte } from './rollen';
import { EIG_GEAENDERT, ladeKern } from './useEigentuemerDetail';

export interface EigKopf {
  name: string;
  firma: boolean;
  einheiten: number;
  werte: Pick<RollenWerte, 'verwaltet' | 'hasWEG' | 'extern' | 'hasMV' | 'hasSEV'>;
  anzahl: { objekte: number; mieter: number; termine: number | null; dokumente: number | null; notizen: number | null; beteiligte: number | null; freigaben: number | null };
}

export type EigKopfZustand = { status: 'laedt' } | { status: 'fehlt' } | { status: 'fehler' } | { status: 'ok'; kopf: EigKopf };

const zahl = (r: { count: number | null; error: unknown }): number | null => (r.error ? null : r.count ?? 0);

export function useEigentuemerKopf(eigentuemerId: string | null): EigKopfZustand {
  const [zustand, setZustand] = useState<EigKopfZustand>({ status: 'laedt' });
  const [runde, setRunde] = useState(0);

  useEffect(() => {
    if (!eigentuemerId) return;
    let aktiv = true;
    setZustand({ status: 'laedt' });
    (async () => {
      const kern = await ladeKern(eigentuemerId);
      if (!aktiv) return;
      if (!kern) { setZustand({ status: 'fehlt' }); return; }
      const heute = heuteIso();
      const z = (tabelle: string) => supabase.from(tabelle).select('id', { count: 'exact', head: true });
      const einheiten = kern.einheitEig.filter(r => istAktuell(r, heute)).map(r => r.einheit_id);
      const [t, d, n, b, f, bt] = await Promise.all([
        z('termine').eq('eigentuemer_id', eigentuemerId).gte('start_datum', heute),
        z('dokumente').eq('eigentuemer_id', eigentuemerId).eq('is_deleted', false),
        z('kontakt_notizen').eq('quelle', 'eigentuemer').eq('quelle_id', eigentuemerId),
        einheiten.length
          ? supabase.from('einheit_eigentuemer').select('eigentuemer_id, vertrag_ab, vertrag_bis').in('einheit_id', einheiten).neq('eigentuemer_id', eigentuemerId)
          : Promise.resolve({ data: [] as { eigentuemer_id: string; vertrag_ab: string | null; vertrag_bis: string | null }[], error: null }),
        z('eigentuemer_freigaben').eq('eigentuemer_id', eigentuemerId).eq('status', 'offen'),
        z('eigentuemer_beteiligte').eq('eigentuemer_id', eigentuemerId),
      ]);
      if (!aktiv) return;
      const p = portfolioWerte(kern.zeile.zuordnungen);
      const mit = b.error ? null : new Set(((b.data ?? []) as { eigentuemer_id: string; vertrag_ab: string | null; vertrag_bis: string | null }[])
        .filter(r => istAktuell(r, heute)).map(r => r.eigentuemer_id)).size;
      setZustand({
        status: 'ok',
        kopf: {
          name: kern.zeile.name,
          firma: kern.zeile.firma,
          einheiten: kern.zeile.werte.nEinheiten,
          werte: kern.zeile.werte,
          anzahl: { objekte: p.objekte, mieter: p.vermietet, termine: zahl(t), dokumente: zahl(d), notizen: zahl(n), beteiligte: mit === null || zahl(bt) === null ? null : mit + (zahl(bt) ?? 0), freigaben: zahl(f) },
        },
      });
    })().catch(() => { if (aktiv) setZustand({ status: 'fehler' }); });
    return () => { aktiv = false; };
  }, [eigentuemerId, runde]);

  useEffect(() => {
    if (!eigentuemerId) return;
    const hoeren = (e: Event) => { if ((e as CustomEvent<string>).detail === eigentuemerId) setRunde(x => x + 1); };
    window.addEventListener(EIG_GEAENDERT, hoeren);
    return () => window.removeEventListener(EIG_GEAENDERT, hoeren);
  }, [eigentuemerId]);

  return zustand;
}
